import { useEffect, useState } from 'react';

export interface LoadingStep {
  id: string;
  label: string;
  status: 'waiting' | 'active' | 'done' | 'error';
  detail?: string;
}

interface Props {
  stage: string;
  steps: LoadingStep[];
  progress: number;
  subtitle?: string;
  creditReassurance?: boolean;
  footerText?: string;
}

export function useLoadingSteps(labels: string[], running: boolean) {
  const [steps, setSteps] = useState<LoadingStep[]>(
    labels.map((label, i) => ({ id: String(i), label, status: 'waiting' }))
  );
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!running) return;
    setSteps(labels.map((label, i) => ({ id: String(i), label, status: 'waiting' })));
    setProgress(0);

    const timers: ReturnType<typeof setTimeout>[] = [];
    const stepDuration = 1800;

    labels.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setSteps(prev => prev.map((s, j) =>
          j === i ? { ...s, status: 'active' } :
          j < i ? { ...s, status: 'done' } : s
        ));
      }, i * stepDuration));

      timers.push(setTimeout(() => {
        setSteps(prev => prev.map((s, j) =>
          j === i ? { ...s, status: 'done' } : s
        ));
      }, i * stepDuration + stepDuration - 300));
    });

    // Smooth progress: advance toward 95% using easing intervals
    let prog = 0;
    const tick = setInterval(() => {
      prog += (95 - prog) * 0.08;
      setProgress(Math.round(prog));
      if (prog >= 94) clearInterval(tick);
    }, 300);
    timers.push(tick as unknown as ReturnType<typeof setTimeout>);

    return () => { timers.forEach(clearTimeout); clearInterval(tick); };
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    setSteps(labels.map((label, i) => ({ id: String(i), label, status: 'done' })));
    setProgress(100);
  };

  return { steps, progress, finish };
}

const STATUS_ICON: Record<LoadingStep['status'], string> = {
  waiting: '○',
  active: '◉',
  done: '✓',
  error: '✗',
};
const STATUS_COLOR: Record<LoadingStep['status'], string> = {
  waiting: '#444466',
  active: '#7c6ff7',
  done: '#00e676',
  error: '#ff4466',
};

export default function LoadingCinematic({ stage, steps, progress, subtitle, creditReassurance, footerText }: Props) {
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(true), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '48px 24px',
      animation: 'fadeIn 0.4s ease',
    }}>
      {/* Rotating gradient ring */}
      <div style={{ position: 'relative', width: 80, height: 80, marginBottom: 32 }}>
        <div style={{
          width: 80, height: 80,
          borderRadius: '50%',
          background: 'conic-gradient(from 0deg, #7c6ff7, #00d4ff, rgba(124,111,247,0.1), #7c6ff7)',
          animation: 'spin 1.2s linear infinite',
          position: 'absolute',
        }} />
        <div style={{
          width: 60, height: 60,
          background: '#050510',
          borderRadius: '50%',
          position: 'absolute',
          top: 10, left: 10,
        }} />
      </div>

      {/* Stage label */}
      <div style={{
        fontSize: 22,
        fontWeight: 700,
        background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: 8,
        textAlign: 'center',
      }}>
        {stage}
      </div>

      {subtitle && (
        <div style={{ color: '#8888aa', fontSize: 14, marginBottom: 32, textAlign: 'center' }}>
          {subtitle}
        </div>
      )}

      {/* Steps list */}
      <div style={{ width: '100%', maxWidth: 360, marginBottom: 28 }}>
        {steps.map((step, i) => (
          <div
            key={step.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 0',
              animation: `slide-right 0.3s ${i * 0.08}s ease both`,
              opacity: step.status === 'waiting' ? 0.4 : 1,
              transition: 'opacity 0.3s ease',
            }}
          >
            <span style={{
              fontSize: step.status === 'active' ? 16 : 14,
              color: STATUS_COLOR[step.status],
              width: 20,
              textAlign: 'center',
              flexShrink: 0,
              animation: step.status === 'active' ? 'glow-pulse 1s ease infinite' : undefined,
            }}>
              {STATUS_ICON[step.status]}
            </span>
            <span style={{
              fontSize: 14,
              color: step.status === 'done' ? '#f0f0ff' : step.status === 'active' ? '#f0f0ff' : '#8888aa',
              fontWeight: step.status === 'active' ? 600 : 400,
            }}>
              {step.label}
              {step.status === 'active' && (
                <span style={{ color: '#7c6ff7' }}>{' '}...</span>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%',
        maxWidth: 360,
        height: 3,
        background: 'rgba(255,255,255,0.06)',
        borderRadius: 2,
        overflow: 'hidden',
        marginBottom: 16,
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #7c6ff7, #00d4ff)',
          borderRadius: 2,
          transition: 'width 0.6s ease',
        }} />
      </div>

      {showHint && (
        <div style={{
          color: '#444466',
          fontSize: 13,
          animation: 'fadeIn 0.5s ease',
        }}>
          {footerText || 'This usually takes a few seconds...'}
        </div>
      )}

      {creditReassurance && (
        <div style={{
          color: '#444466',
          fontSize: 12,
          textAlign: 'center',
          marginTop: 16,
          animation: 'fadeIn 0.5s ease',
        }}>
          ⚡ Using your free pipeline credits · 0 credits charged for this step
        </div>
      )}
    </div>
  );
}
